import { useSelector } from "react-redux";
import { useCallback, useState } from "react";
import { sharedLib } from "@shared/services/shared.service";
import { useGeetest } from "utils/hooks/useGeetest";
import {
  DisplayResultMessage,
  RequestSource,
  ResendRegistrationEmailRequest,
  VerifyConfirmEmailCodeRequest,
  LoginResponseWithUserId,
  LanguageNameSpaces,
} from "@shared/types";
import { handleUserTempOAuthData } from "@shared/features/onboarding/utils";
import { useBranding } from "@shared/utils/hooks/useBranding.ts";
import {
  completeVerificationAnalytics,
  replaceVerificationChallenge,
} from "analytics/client";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";

interface useVerifyEmailProps {
  emailValidatorUUID?: string | null;
  displayResultMessage: DisplayResultMessage;
  onSubmitSuccess?: (redirectToOAuth?: boolean) => void;
  onResendSuccess?: (emailValidatorUUID: string) => void;
  storageEmail?: string | null;
  mobileIdentifier?: string;
  uniqueIdentifier?: string;
}

const useVerifyEmail = ({
  emailValidatorUUID,
  displayResultMessage,
  onSubmitSuccess,
  onResendSuccess,
  storageEmail,
  mobileIdentifier,
  uniqueIdentifier,
}: useVerifyEmailProps) => {
  const { isWhitelabel } = useBranding();
  const { t } = useTypedTranslation(LanguageNameSpaces.AUTH);
  const user = useSelector(sharedLib.selectors.authentication.selectUser);
  const useEmailValidatorUUID = !!emailValidatorUUID;
  const [verifyConfirmEmailCode, verifyConfirmEmailCodeStatus] =
    sharedLib.sharedApi.authentication.useVerifyConfirmEmailCodeMutation();
  const [verifyConfirmEmailCodeByUUID, verifyConfirmEmailCodeByUUIDStatus] =
    sharedLib.sharedApi.authentication.useVerifyConfirmEmailCodeByUUIDMutation();
  const [resendRegistrationEmail, resendRegistrationEmailStatus] =
    sharedLib.sharedApi.authentication.useResendRegistrationEmailMutation();
  const [getUser, getUserStatus] =
    sharedLib.sharedApi.authentication.useLazyGetUserQuery();
  const [showSupportLink, setShowSupportLink] = useState(false);

  const { verify: onResendCode, isLoading: isCaptchaLoading } = useGeetest({
    onSuccess: (captcha_token) => {
      handleResendCode(captcha_token);
    },
  });

  const email = useEmailValidatorUUID
    ? storageEmail
    : user?.email || storageEmail;

  const onSubmit = useCallback(
    (code: string) => {
      if (code) {
        const request: VerifyConfirmEmailCodeRequest = {
          auth_code: parseInt(code),
          request_source: RequestSource.WEBSITE,
          mobile_identifier: mobileIdentifier,
          unique_identifier: uniqueIdentifier,
        };
        if (useEmailValidatorUUID) {
          request.email_validator_uuid = emailValidatorUUID;
        }
        const func = useEmailValidatorUUID
          ? verifyConfirmEmailCodeByUUID
          : verifyConfirmEmailCode;
        displayResultMessage(
          func(request as any).unwrap(),
          t("verify_email.success", { email }),
          t("verify_email.wrong_code")
        )
          .then(async (response: LoginResponseWithUserId) => {
            if (emailValidatorUUID && response.user_id)
              completeVerificationAnalytics(
                emailValidatorUUID,
                String(response.user_id)
              );
            const redirectToOAuth = handleUserTempOAuthData(response.user_id);
            onSubmitSuccess?.(redirectToOAuth);
            getUser().catch(() => {});
          })
          .catch(() => {});
      }
    },
    [
      emailValidatorUUID,
      useEmailValidatorUUID,
      mobileIdentifier,
      uniqueIdentifier,
    ]
  );

  const handleResendCode = (captcha_token?: string) => {
    setShowSupportLink(true);

    const request: ResendRegistrationEmailRequest = {
      request_source: RequestSource.WEBSITE,
      captcha_token,
      is_whitelabel: isWhitelabel,
    };
    if (useEmailValidatorUUID) {
      request.email_validator_uuid = emailValidatorUUID;
    } else {
      request.email = user?.email;
    }
    displayResultMessage(resendRegistrationEmail(request).unwrap())
      .then((response) => {
        const nextValidatorUUID = response.data.email_validator_uuid;
        if (useEmailValidatorUUID && emailValidatorUUID && nextValidatorUUID) {
          replaceVerificationChallenge(emailValidatorUUID, nextValidatorUUID);
          onResendSuccess?.(nextValidatorUUID);
        }
      })
      .catch(() => {});
  };

  const verificationCodeLoading =
    getUserStatus.isFetching ||
    verifyConfirmEmailCodeStatus.isLoading ||
    verifyConfirmEmailCodeByUUIDStatus.isLoading;
  const codeText = email
    ? t("verify_email.code_text_with_email", { email })
    : t("verify_email.code_text");

  return {
    onResendCode,
    onSubmit,
    showSupportLink,
    codeText,
    resendLoading: resendRegistrationEmailStatus.isLoading || isCaptchaLoading,
    verificationCodeLoading,
  };
};

export default useVerifyEmail;
